import React, { useState, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Appointment, AppointmentStatus } from '../types/appointment'
import { Patient } from '../types/patient'
import { User } from '../types/user'
import { clinicHoursService } from '../services/clinicHoursService'
import { appointmentService } from '../services/appointmentService'
import { appointmentTypesService } from '../services/appointmentTypesService'
import { AppointmentTypeConfig } from '../types/appointmentType'
import { Modal } from './Modal'
import { AppointmentScheduler } from './AppointmentScheduler'
import { AppointmentCancelModal } from './AppointmentCancelModal'
import { AppointmentDNAModal } from './AppointmentDNAModal'
import { QuickStartConsultationForm } from './QuickStartConsultationForm'
import { StcSigning } from '../pages/StcSigning'
import { formatPatientName, formatUserName } from '../utils/name'
import { mapStcReasonsToSpecialServiceCode } from '../utils/stcReasonToCode'
import './PractitionerAvailability.css'

interface PractitionerAvailabilityProps {
  appointments: Appointment[]
  patients: Patient[]
  staff: User[]
  onAppointmentClick?: (appointment: Appointment) => void
}

type ViewType = 'day' | 'week'

export function PractitionerAvailability({
  appointments,
  patients,
  staff,
  onAppointmentClick,
}: PractitionerAvailabilityProps) {
  const [viewType, setViewType] = useState<ViewType>('day')
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const today = new Date()
    return today.toISOString().split('T')[0]
  })
  const [showBookingModal, setShowBookingModal] = useState(false)
  const [selectedSlot, setSelectedSlot] = useState<{
    staffId: string
    date: string
    time: string
  } | null>(null)
  const [cancelModalAppointment, setCancelModalAppointment] = useState<Appointment | null>(null)
  const [dnaModalAppointment, setDnaModalAppointment] = useState<Appointment | null>(null)
  const [consultationModalAppointment, setConsultationModalAppointment] = useState<Appointment | null>(null)
  const [stcModalAppointment, setStcModalAppointment] = useState<Appointment | null>(null)
  const queryClient = useQueryClient()

  const clinicalStaff = useMemo(
    () => staff.filter((s) => s.roleCategory === 'Clinical'),
    [staff]
  )

  // Get clinic hours for date range
  const { data: clinicHours } = useQuery({
    queryKey: ['clinic-hours'],
    queryFn: () => clinicHoursService.getCurrent(),
  })

  // Get appointment types for color mapping
  const { data: appointmentTypes } = useQuery({
    queryKey: ['appointment-types'],
    queryFn: () => appointmentTypesService.getAll(),
    refetchInterval: 10000, // Poll every 10 seconds to get real-time updates from other users
    refetchOnWindowFocus: true, // Refetch when user returns to the tab
    refetchIntervalInBackground: false, // Only poll when tab is active to save resources
  })

  const cancelMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      appointmentService.cancel(id, reason),
    onMutate: async ({ id }) => {
      // Cancel any outgoing refetches to avoid overwriting optimistic update
      // Update all appointment queries (with or without filters)
      await queryClient.cancelQueries({ queryKey: ['appointments'] })
      
      // Get all appointment queries and update them optimistically
      const queryCache = queryClient.getQueryCache()
      const appointmentQueries = queryCache.findAll({ queryKey: ['appointments'] })
      
      const previousData = new Map()
      
      // Optimistically update all appointment queries
      appointmentQueries.forEach((query) => {
        const previousAppointments = query.state.data as Appointment[] | undefined
        if (previousAppointments) {
          previousData.set(query.queryKey, previousAppointments)
          queryClient.setQueryData<Appointment[]>(query.queryKey, (old) => {
            if (!old) return old
            return old.map(apt => 
              apt.id === id 
                ? { ...apt, status: 'cancelled' as AppointmentStatus, cancelledAt: new Date().toISOString() }
                : apt
            )
          })
        }
      })
      
      return { previousData }
    },
    onSuccess: async () => {
      // Refetch all appointment queries to get the latest data from server
      await queryClient.refetchQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['available-slots'] })
      setCancelModalAppointment(null)
    },
    onError: (error, _variables, context) => {
      // Rollback on error
      if (context?.previousData) {
        context.previousData.forEach((data, queryKey) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      console.error('Failed to cancel appointment:', error)
      alert('Failed to cancel appointment. Please try again.')
    },
  })

  const handleCancel = (appointment: Appointment, e: React.MouseEvent) => {
    e.stopPropagation()
    setCancelModalAppointment(appointment)
  }

  const handleCancelConfirm = async (reason?: string) => {
    if (cancelModalAppointment) {
      await cancelMutation.mutateAsync({ id: cancelModalAppointment.id, reason })
    }
  }

  const dnaMutation = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      appointmentService.markAsDNA(id, reason),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ['appointments'] })
      
      const queryCache = queryClient.getQueryCache()
      const appointmentQueries = queryCache.findAll({ queryKey: ['appointments'] })
      
      const previousData = new Map()
      
      appointmentQueries.forEach((query) => {
        const previousAppointments = query.state.data as Appointment[] | undefined
        if (previousAppointments) {
          previousData.set(query.queryKey, previousAppointments)
          queryClient.setQueryData<Appointment[]>(query.queryKey, (old) => {
            if (!old) return old
            return old.map(apt => 
              apt.id === id 
                ? { ...apt, status: 'dna' as AppointmentStatus, dnaAt: new Date().toISOString() }
                : apt
            )
          })
        }
      })
      
      return { previousData }
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['appointments'] })
      await queryClient.refetchQueries({ queryKey: ['patients'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      queryClient.invalidateQueries({ queryKey: ['available-slots'] })
      setDnaModalAppointment(null)
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        context.previousData.forEach((data, queryKey) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      console.error('Failed to mark appointment as DNA:', error)
      alert('Failed to mark appointment as DNA. Please try again.')
    },
  })

  const handleMarkDNA = (appointment: Appointment, e: React.MouseEvent) => {
    e.stopPropagation()
    setDnaModalAppointment(appointment)
  }

  const arrivedMutation = useMutation({
    mutationFn: (id: string) => appointmentService.markAsArrived(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['appointments'] })
      
      const queryCache = queryClient.getQueryCache()
      const appointmentQueries = queryCache.findAll({ 
        queryKey: ['appointments'],
        exact: false 
      })
      
      const previousData = new Map()
      
      appointmentQueries.forEach((query) => {
        const previousAppointments = query.state.data as Appointment[] | undefined
        if (previousAppointments) {
          previousData.set(query.queryKey, previousAppointments)
          queryClient.setQueryData<Appointment[]>(query.queryKey, (old) => {
            if (!old) return old
            const updated = old.map(apt => {
              if (apt.id === id) {
                console.log('[Debug] Optimistic update - changing status from', apt.status, 'to arrived for:', id)
                return { ...apt, status: 'arrived' as AppointmentStatus, arrivedAt: new Date().toISOString() }
              }
              return apt
            })
            console.log('[Debug] Updated appointments count:', updated.length, 'Query key:', query.queryKey)
            return updated
          })
        }
      })
      
      return { previousData }
    },
    onSuccess: async () => {
      console.log('[Debug] arrivedMutation onSuccess - invalidating queries')
      // Invalidate all appointment queries to ensure UI updates immediately
      await queryClient.invalidateQueries({ 
        queryKey: ['appointments'],
        exact: false 
      })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
      console.log('[Debug] arrivedMutation onSuccess - queries invalidated')
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        context.previousData.forEach((data, queryKey) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      console.error('Failed to mark appointment as arrived:', error)
      alert('Failed to mark appointment as arrived. Please try again.')
    },
  })

  const revertArrivedMutation = useMutation({
    mutationFn: (id: string) => appointmentService.revertArrived(id),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: ['appointments'] })
      
      const queryCache = queryClient.getQueryCache()
      const appointmentQueries = queryCache.findAll({ queryKey: ['appointments'] })
      
      const previousData = new Map()
      
      appointmentQueries.forEach((query) => {
        const previousAppointments = query.state.data as Appointment[] | undefined
        if (previousAppointments) {
          previousData.set(query.queryKey, previousAppointments)
          queryClient.setQueryData<Appointment[]>(query.queryKey, (old) => {
            if (!old) return old
            return old.map(apt => 
              apt.id === id 
                ? { ...apt, status: 'scheduled' as AppointmentStatus, arrivedAt: undefined, arrivedBy: undefined }
                : apt
            )
          })
        }
      })
      
      return { previousData }
    },
    onSuccess: async () => {
      await queryClient.refetchQueries({ queryKey: ['appointments'] })
      queryClient.invalidateQueries({ queryKey: ['dashboard'] })
    },
    onError: (error, _variables, context) => {
      if (context?.previousData) {
        context.previousData.forEach((data, queryKey) => {
          queryClient.setQueryData(queryKey, data)
        })
      }
      console.error('Failed to revert arrived status:', error)
      alert('Failed to revert arrived status. Please try again.')
    },
  })

  const handleMarkArrived = async (appointment: Appointment, e: React.MouseEvent) => {
    e.stopPropagation()
    console.log('[Debug] handleMarkArrived called for appointment:', {
      id: appointment.id,
      currentStatus: appointment.status,
      patientId: appointment.patientId
    })
    if (window.confirm(`Mark ${formatPatientName(patients.find(p => p.id === appointment.patientId) as any)} as arrived?`)) {
      console.log('[Debug] Confirmed, calling mutation for:', appointment.id)
      await arrivedMutation.mutateAsync(appointment.id)
      console.log('[Debug] Mutation completed for:', appointment.id)
    }
  }

  const handleRevertArrived = async (appointment: Appointment, e: React.MouseEvent) => {
    e.stopPropagation()
    if (window.confirm(`Revert ${formatPatientName(patients.find(p => p.id === appointment.patientId) as any)}'s arrived status?`)) {
      await revertArrivedMutation.mutateAsync(appointment.id)
    }
  }

  const handleStartConsultation = (appointment: Appointment, e: React.MouseEvent) => {
    e.stopPropagation()
    setConsultationModalAppointment(appointment)
  }

  const handleDNAConfirm = async (reason?: string) => {
    if (dnaModalAppointment) {
      await dnaMutation.mutateAsync({ id: dnaModalAppointment.id, reason })
    }
  }

  // Calculate date range based on view type
  const dateRange = useMemo(() => {
    const dates: string[] = []
    const startDate = new Date(selectedDate)
    
    if (viewType === 'day') {
      dates.push(selectedDate)
    } else {
      // Week view: start from Monday of the week
      const dayOfWeek = startDate.getDay()
      const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek
      const monday = new Date(startDate)
      monday.setDate(startDate.getDate() + mondayOffset)
      
      for (let i = 0; i < 7; i++) {
        const date = new Date(monday)
        date.setDate(monday.getDate() + i)
        dates.push(date.toISOString().split('T')[0])
      }
    }
    
    return dates
  }, [selectedDate, viewType])

  // Get clinic hours for a specific date
  const getClinicHoursForDate = useCallback((date: string): { startTime: string; endTime: string; isOpen: boolean; closedPeriods?: Array<{ startTime: string; endTime: string }> } | null => {
    if (!clinicHours) {
      return { startTime: '08:00', endTime: '18:00', isOpen: true } // Default fallback
    }
    
    const dateObj = new Date(date + 'T12:00:00')
    const dayOfWeek = dateObj.getDay()
    const dayHours = clinicHours.days.find((d) => d.dayOfWeek === dayOfWeek)
    
    if (!dayHours || !dayHours.isOpen) {
      return null // Clinic is closed
    }
    
    return {
      startTime: dayHours.startTime,
      endTime: dayHours.endTime,
      isOpen: true,
      closedPeriods: dayHours.closedPeriods,
    }
  }, [clinicHours])

  // Check if a time slot is in a closed period
  const isInClosedPeriod = (time: string, closedPeriods?: Array<{ startTime: string; endTime: string }>): boolean => {
    if (!closedPeriods || closedPeriods.length === 0) return false
    
    const [slotH, slotM] = time.split(':').map(Number)
    const slotMinutes = slotH * 60 + slotM
    
    return closedPeriods.some(period => {
      const [startH, startM] = period.startTime.split(':').map(Number)
      const [endH, endM] = period.endTime.split(':').map(Number)
      const startMinutes = startH * 60 + startM
      const endMinutes = endH * 60 + endM
      
      return slotMinutes >= startMinutes && slotMinutes < endMinutes
    })
  }

  // Generate time slots based on clinic hours
  // For day view: use that day's hours
  // For week view: use the union of all days' hours (earliest start to latest end)
  const timeSlots = useMemo(() => {
    const slots: string[] = []
    const interval = 15 // 15-minute intervals
    
    if (dateRange.length === 0) return slots
    
    // Get clinic hours for all dates in range
    const allHours = dateRange.map(date => getClinicHoursForDate(date)).filter(Boolean) as Array<{ startTime: string; endTime: string; isOpen: boolean }>
    
    if (allHours.length === 0) {
      // No clinic hours configured, use defaults
      const defaultStart = '08:00'
      const defaultEnd = '18:00'
      const [startH, startM] = defaultStart.split(':').map(Number)
      const [endH, endM] = defaultEnd.split(':').map(Number)
      let total = startH * 60 + startM
      const endTotal = endH * 60 + endM
      
      while (total < endTotal) {
        const h = Math.floor(total / 60)
        const m = total % 60
        slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
        total += interval
      }
      return slots
    }
    
    // Find earliest start time and latest end time across all days
    let earliestStart = allHours[0].startTime
    let latestEnd = allHours[0].endTime
    
    for (const hours of allHours) {
      const [currentStartH, currentStartM] = hours.startTime.split(':').map(Number)
      const [currentEndH, currentEndM] = hours.endTime.split(':').map(Number)
      const [earliestStartH, earliestStartM] = earliestStart.split(':').map(Number)
      const [latestEndH, latestEndM] = latestEnd.split(':').map(Number)
      
      const currentStartMinutes = currentStartH * 60 + currentStartM
      const currentEndMinutes = currentEndH * 60 + currentEndM
      const earliestStartMinutes = earliestStartH * 60 + earliestStartM
      const latestEndMinutes = latestEndH * 60 + latestEndM
      
      if (currentStartMinutes < earliestStartMinutes) {
        earliestStart = hours.startTime
      }
      if (currentEndMinutes > latestEndMinutes) {
        latestEnd = hours.endTime
      }
    }
    
    // Generate slots from earliest start to latest end
    const [startH, startM] = earliestStart.split(':').map(Number)
    const [endH, endM] = latestEnd.split(':').map(Number)
    let total = startH * 60 + startM
    const endTotal = endH * 60 + endM
    
    while (total < endTotal) {
      const h = Math.floor(total / 60)
      const m = total % 60
      slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`)
      total += interval
    }
    
    return slots
  }, [clinicHours, dateRange, getClinicHoursForDate])

  // Filter out cancelled and DNA appointments for calendar display
  // This ensures cancelled/DNA appointments are immediately removed from the calendar
  // and their slots become available again
  const activeAppointments = useMemo(() => {
    return appointments.filter((a) => a.status !== 'cancelled' && a.status !== 'dna')
  }, [appointments])

  // Organize appointments by staff and date
  const appointmentsByStaffAndDate = useMemo(() => {
    const result: Record<string, Record<string, Appointment[]>> = {}
    
    clinicalStaff.forEach((s) => {
      result[s.id] = {}
      dateRange.forEach((date) => {
        result[s.id][date] = activeAppointments.filter(
          (a) => a.staffId === s.id && a.appointmentDate === date
        )
      })
    })
    
    return result
  }, [activeAppointments, clinicalStaff, dateRange])

  // Find appointment at a specific time slot (returns first match for backward compatibility)
  const findAppointmentAtSlot = (
    staffId: string,
    date: string,
    time: string
  ): Appointment | undefined => {
    const appointments = findAppointmentsAtSlot(staffId, date, time)
    return appointments[0] // Return first appointment for backward compatibility
  }

  // Find all appointments at a specific time slot
  const findAppointmentsAtSlot = useCallback((
    staffId: string,
    date: string,
    time: string
  ): Appointment[] => {
    const staffAppts = appointmentsByStaffAndDate[staffId]?.[date] || []
    const result = staffAppts.filter((a) => {
      const [startH, startM] = a.startTime.split(':').map(Number)
      const [slotH, slotM] = time.split(':').map(Number)
      const startMinutes = startH * 60 + startM
      const slotMinutes = slotH * 60 + slotM
      const durationMinutes = a.duration || 30
      
      // Check if slot falls within appointment time range
      return slotMinutes >= startMinutes && slotMinutes < startMinutes + durationMinutes
    })
    
    // Debug: Log if we find overlap appointments
    const overlapAppts = result.filter(apt => apt.id.includes('overlap'))
    if (overlapAppts.length > 0) {
      console.log('[Debug] findAppointmentsAtSlot found overlap appointments:', {
        staffId,
        date,
        time,
        count: result.length,
        statuses: result.map(apt => ({ id: apt.id, status: apt.status }))
      })
    }
    
    return result
  }, [appointmentsByStaffAndDate])

  // Check if a slot is the START of an appointment (not just any slot within it)
  const isAppointmentStart = (
    staffId: string,
    date: string,
    time: string
  ): boolean => {
    const appointment = findAppointmentAtSlot(staffId, date, time)
    if (!appointment) return false
    
    const [startH, startM] = appointment.startTime.split(':').map(Number)
    const [slotH, slotM] = time.split(':').map(Number)
    const startMinutes = startH * 60 + startM
    const slotMinutes = slotH * 60 + slotM
    
    return slotMinutes === startMinutes
  }

  // Check if a slot is the END of an appointment (the last slot within it)
  const isAppointmentEnd = (
    staffId: string,
    date: string,
    time: string,
    appointment?: Appointment
  ): boolean => {
    const appt = appointment || findAppointmentAtSlot(staffId, date, time)
    if (!appt) return false
    
    const [endH, endM] = appt.endTime.split(':').map(Number)
    const [slotH, slotM] = time.split(':').map(Number)
    const endMinutes = endH * 60 + endM
    const slotMinutes = slotH * 60 + slotM
    
    // Check if this slot's end time matches the appointment's end time
    // Since slots are 15 minutes, the slot ends at slotMinutes + 15
    return slotMinutes + 15 === endMinutes
  }

  // Get color for appointment based on type
  const getAppointmentColor = (type: string): { background: string; border: string; hoverBackground: string; hoverBorder: string } => {
    // Default fallback colors
    const defaultColors = {
      background: '#f3f4f6',
      border: '#d1d5db',
      hoverBackground: '#e5e7eb',
      hoverBorder: '#9ca3af',
    }

    if (!appointmentTypes || appointmentTypes.length === 0) {
      return defaultColors
    }

    // Find the appointment type configuration
    const typeConfig = appointmentTypes.find((at: AppointmentTypeConfig) => at.code === type && at.isActive)
    
    if (!typeConfig) {
      return defaultColors
    }

    return {
      background: typeConfig.colors.background,
      border: typeConfig.colors.border,
      hoverBackground: typeConfig.colors.hoverBackground,
      hoverBorder: typeConfig.colors.hoverBorder,
    }
  }

  // Handle hover on appointment slots
  const [hoveredAppointmentId, setHoveredAppointmentId] = useState<string | null>(null)

  const handleSlotMouseEnter = (appointment: Appointment | null) => {
    if (appointment) {
      setHoveredAppointmentId(appointment.id)
    }
  }

  const handleSlotMouseLeave = () => {
    setHoveredAppointmentId(null)
  }

  // Get slot status: 'available' | 'booked' | 'closed' | 'unavailable'
  const getSlotStatus = (staffId: string, date: string, time: string): 'available' | 'booked' | 'closed' | 'unavailable' => {
    // Check if slot is already booked (has any appointments)
    const appointments = findAppointmentsAtSlot(staffId, date, time)
    if (appointments.length > 0) {
      return 'booked'
    }

    // Check if clinic is open on this day and if time slot is within clinic hours
    const dayHours = getClinicHoursForDate(date)
    
    if (!dayHours || !dayHours.isOpen) {
      return 'closed' // Clinic is closed on this day
    }

    // Check if time slot is within clinic hours for this specific day
    const [slotH, slotM] = time.split(':').map(Number)
    const slotMinutes = slotH * 60 + slotM
    const [startH, startM] = dayHours.startTime.split(':').map(Number)
    const [endH, endM] = dayHours.endTime.split(':').map(Number)
    const startMinutes = startH * 60 + startM
    const endMinutes = endH * 60 + endM

    if (slotMinutes < startMinutes || slotMinutes >= endMinutes) {
      return 'closed' // Time slot is outside clinic hours (clinic is closed at this time)
    }

    // Check if time slot is in a closed period (e.g., lunch break)
    if (isInClosedPeriod(time, dayHours.closedPeriods)) {
      return 'closed' // Time slot is in a closed period
    }

    return 'available'
  }

  const handleSlotClick = (staffId: string, date: string, time: string) => {
    const status = getSlotStatus(staffId, date, time)
    if (status === 'available') {
      setSelectedSlot({ staffId, date, time })
      setShowBookingModal(true)
    } else if (status === 'booked') {
      const appointment = findAppointmentAtSlot(staffId, date, time)
      if (appointment && onAppointmentClick) {
        onAppointmentClick(appointment)
      }
    }
  }

  const handleDateNavigation = (direction: 'prev' | 'next') => {
    const currentDate = new Date(selectedDate)
    const offset = viewType === 'day' ? 1 : 7
    currentDate.setDate(currentDate.getDate() + (direction === 'next' ? offset : -offset))
    setSelectedDate(currentDate.toISOString().split('T')[0])
  }

  const formatDateHeader = (dateString: string): string => {
    const date = new Date(dateString + 'T12:00:00')
    const today = new Date()
    today.setHours(12, 0, 0, 0)
    const dateOnly = new Date(date)
    dateOnly.setHours(12, 0, 0, 0)
    
    const isToday = dateOnly.getTime() === today.getTime()
    const dayName = date.toLocaleDateString('en-US', { weekday: 'short' })
    const monthDay = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    
    return `${dayName} ${monthDay}${isToday ? ' (Today)' : ''}`
  }

  const formatWeekRange = (): string => {
    if (viewType === 'day') return formatDateHeader(selectedDate)
    
    const start = new Date(dateRange[0] + 'T12:00:00')
    const end = new Date(dateRange[dateRange.length - 1] + 'T12:00:00')
    const startStr = start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
    const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    
    return `${startStr} - ${endStr}`
  }

  return (
    <div className="practitioner-availability">
      <div className="pa-header">
        <div className="pa-title-section">
          <h2 className="pa-title">Practitioner Availability</h2>
          <div className="pa-date-range">{formatWeekRange()}</div>
        </div>
        
        <div className="pa-controls">
          <div className="pa-view-toggle">
            <button
              className={`pa-view-btn ${viewType === 'day' ? 'active' : ''}`}
              onClick={() => setViewType('day')}
            >
              Day
            </button>
            <button
              className={`pa-view-btn ${viewType === 'week' ? 'active' : ''}`}
              onClick={() => setViewType('week')}
            >
              Week
            </button>
          </div>
          
          <div className="pa-date-nav">
            <button
              className="pa-nav-btn"
              onClick={() => handleDateNavigation('prev')}
              aria-label="Previous"
            >
              ←
            </button>
            <button
              className="pa-nav-btn"
              onClick={() => {
                const today = new Date().toISOString().split('T')[0]
                setSelectedDate(today)
              }}
            >
              Today
            </button>
            <button
              className="pa-nav-btn"
              onClick={() => handleDateNavigation('next')}
              aria-label="Next"
            >
              →
            </button>
          </div>
          
          <input
            type="date"
            className="pa-date-picker"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
          />
        </div>
      </div>

      <div className="pa-grid-container">
        <div
          className="pa-grid"
          style={{
            gridTemplateColumns: `120px repeat(${dateRange.length * clinicalStaff.length}, 1fr)`,
          }}
        >
          {/* Time column header */}
          <div className="pa-time-header">Time</div>
          
          {/* Date and staff headers */}
          {dateRange.map((date) =>
            clinicalStaff.map((s) => (
              <div key={`${date}-${s.id}`} className="pa-staff-header">
                <div className="pa-staff-date">{formatDateHeader(date)}</div>
                <div className="pa-staff-name">
                  {s.firstName} {s.lastName}
                </div>
              </div>
            ))
          )}

          {/* Time slots */}
          {timeSlots.map((time) => (
            <React.Fragment key={`time-row-${time}`}>
              <div className="pa-time-cell">
                {time}
              </div>
              {dateRange.map((date) =>
                clinicalStaff.map((s) => {
                  const appointments = findAppointmentsAtSlot(s.id, date, time)
                  const slotStatus = getSlotStatus(s.id, date, time)
                  const hasMultipleAppointments = appointments.length > 1
                  
                  // For multiple appointments, we'll show them stacked
                  // For single appointment, use existing logic
                  const primaryAppointment = appointments[0]
                  const isStart = primaryAppointment ? isAppointmentStart(s.id, date, time) : false
                  const isEnd = primaryAppointment ? isAppointmentEnd(s.id, date, time, primaryAppointment) : false
                  const isContinuation = slotStatus === 'booked' && !isStart && !hasMultipleAppointments
                  
                  const isHovered = slotStatus === 'booked' && primaryAppointment && hoveredAppointmentId === primaryAppointment.id
                  const isStartBlockHovered = slotStatus === 'booked' && primaryAppointment && isStart && hoveredAppointmentId === primaryAppointment.id
                  const appointmentColor = primaryAppointment ? getAppointmentColor(primaryAppointment.type) : null
                  
                  return (
                    <div
                      key={`${date}-${s.id}-${time}`}
                      className={`pa-slot ${
                        slotStatus === 'booked' 
                          ? 'booked' 
                          : slotStatus === 'available' 
                          ? 'available' 
                          : slotStatus === 'closed'
                          ? 'closed'
                          : 'unavailable'
                      } ${isContinuation ? 'pa-slot-continuation' : ''} ${isHovered ? 'pa-slot-hovered' : ''} ${slotStatus === 'booked' && primaryAppointment && isStart ? 'pa-slot-start' : ''} ${isStartBlockHovered ? 'pa-slot-appointment-hovered' : ''} ${hasMultipleAppointments ? 'pa-slot-multiple' : ''}`}
                      data-appointment-id={slotStatus === 'booked' && primaryAppointment ? primaryAppointment.id : undefined}
                      style={
                        slotStatus === 'booked' && appointmentColor && !hasMultipleAppointments
                          ? {
                              backgroundColor: isHovered ? appointmentColor.hoverBackground : appointmentColor.background,
                              borderColor: isHovered ? appointmentColor.hoverBorder : appointmentColor.border,
                              borderLeftColor: isHovered ? appointmentColor.hoverBorder : appointmentColor.border,
                              borderRightColor: isHovered ? appointmentColor.hoverBorder : appointmentColor.border,
                              borderTop: isContinuation ? 'none' : undefined,
                              borderBottom: !isEnd ? 'none' : undefined,
                              marginTop: isContinuation ? '-1px' : undefined,
                            }
                          : undefined
                      }
                      onMouseEnter={() => handleSlotMouseEnter(primaryAppointment || null)}
                      onMouseLeave={handleSlotMouseLeave}
                      onClick={() => {
                        if (slotStatus === 'available') {
                          handleSlotClick(s.id, date, time)
                        } else if (slotStatus === 'booked' && primaryAppointment) {
                          if (onAppointmentClick) {
                            onAppointmentClick(primaryAppointment)
                          }
                        }
                      }}
                      title={
                        slotStatus === 'booked' && appointments.length > 0
                          ? hasMultipleAppointments
                            ? `${appointments.length} appointments at this time`
                            : `${primaryAppointment.reason || 'Appointment'} • ${primaryAppointment.startTime}-${primaryAppointment.endTime}`
                          : slotStatus === 'available'
                          ? 'Click to book appointment'
                          : slotStatus === 'closed'
                          ? 'Closed'
                          : 'Unavailable'
                      }
                    >
                      {slotStatus === 'booked' && appointments.length > 0 ? (
                        hasMultipleAppointments ? (
                          <div className="pa-slot-multiple-container">
                            {appointments.map((appointment) => {
                              const apptColor = getAppointmentColor(appointment.type)
                              const isApptHovered = hoveredAppointmentId === appointment.id
                              const showActions = appointment.status !== 'cancelled' && 
                                                 appointment.status !== 'completed' && 
                                                 appointment.status !== 'dna'
                              // Explicitly check status - ensure we're reading the current status
                              const appointmentStatus = appointment.status
                              
                              // Debug logging
                              if (appointment.id.includes('overlap')) {
                                console.log('[Debug] Appointment:', {
                                  id: appointment.id,
                                  status: appointmentStatus,
                                  isArrived: appointmentStatus === 'arrived',
                                  showActions,
                                  patientId: appointment.patientId
                                })
                              }
                              
                              return (
                                <div
                                  key={appointment.id}
                                  className={`pa-slot-multiple-item ${isApptHovered ? 'pa-slot-multiple-item-hovered' : ''}`}
                                  style={{
                                    backgroundColor: isApptHovered ? apptColor.hoverBackground : apptColor.background,
                                    borderColor: isApptHovered ? apptColor.hoverBorder : apptColor.border,
                                  }}
                                  onMouseEnter={() => handleSlotMouseEnter(appointment)}
                                  onMouseLeave={handleSlotMouseLeave}
                                  onClick={(e) => {
                                    // Don't trigger appointment click if clicking on action buttons
                                    const target = e.target as HTMLElement
                                    if (target.closest('.pa-slot-actions-multiple') || target.closest('button')) {
                                      console.log('[Debug] Click intercepted - clicked on button or actions container')
                                      return
                                    }
                                    e.stopPropagation()
                                    if (onAppointmentClick) {
                                      onAppointmentClick(appointment)
                                    }
                                  }}
                                  title={`${appointment.reason || 'Appointment'} • ${appointment.startTime}-${appointment.endTime}`}
                                >
                                  <div className="pa-slot-multiple-item-content">
                                    <div className="pa-slot-multiple-item-header">
                                      <div className="pa-slot-title-small">
                                        {formatPatientName(patients.find((p) => p.id === appointment.patientId) as any) || 'Unknown Patient'}
                                        {appointmentStatus === 'arrived' && (
                                          <span className="pa-slot-arrived-badge-small" title="Status: Arrived"> ✓</span>
                                        )}
                                        {appointmentStatus === 'in-progress' && (
                                          <span className="pa-slot-in-progress-badge-small"> ⚡</span>
                                        )}
                                      </div>
                                      {appointmentStatus === 'completed' && appointment.stcSuggested && (
                                        appointment.stcRaised ? (
                                          <span
                                            className="pa-slot-stc-badge-small pa-slot-stc-badge-disabled"
                                            title={appointment.stcRaisedAt ? `STC signed ${new Date(appointment.stcRaisedAt).toLocaleString()}` : 'STC signed'}
                                            aria-label="STC signed"
                                          >
                                            STC
                                          </span>
                                        ) : (
                                          <button
                                            type="button"
                                            className="pa-slot-stc-badge-small pa-slot-stc-badge-button"
                                            title={appointment.stcSuggestedReasons?.length ? `STC signature needed: ${appointment.stcSuggestedReasons.join(', ')}` : 'STC signature needed'}
                                            aria-label="STC signature required – open form"
                                            onClick={(e) => {
                                              e.stopPropagation()
                                              setStcModalAppointment(appointment)
                                            }}
                                          >
                                            STC
                                          </button>
                                        )
                                      )}
                                    </div>
                                    <div className="pa-slot-time-small">
                                      {appointment.startTime}-{appointment.endTime}
                                      {/* Debug: Show status in UI */}
                                      {appointment.id.includes('overlap') && (
                                        <span style={{ fontSize: '0.6rem', color: '#666', marginLeft: '0.25rem' }}>
                                          [{appointmentStatus}]
                                        </span>
                                      )}
                                    </div>
                                    {showActions && (
                                      <div className="pa-slot-actions-multiple">
                                        {(() => {
                                          // Debug: Log what buttons should show
                                          if (appointment.id.includes('overlap')) {
                                            console.log('[Debug] Rendering buttons for:', {
                                              id: appointment.id,
                                              status: appointmentStatus,
                                              isArrived: appointmentStatus === 'arrived',
                                              showActions,
                                              willShowArrivedButtons: appointmentStatus === 'arrived',
                                              willShowMarkArrivedButton: appointmentStatus !== 'arrived' && appointmentStatus !== 'in-progress'
                                            })
                                          }
                                          return null
                                        })()}
                                        {(() => {
                                          // Explicit debug logging
                                          if (appointment.id.includes('overlap')) {
                                            console.log('[Debug] Button rendering decision:', {
                                              id: appointment.id,
                                              appointmentStatus,
                                              rawStatus: appointment.status,
                                              isArrived: appointmentStatus === 'arrived',
                                              isScheduled: appointmentStatus === 'scheduled',
                                              isInProgress: appointmentStatus === 'in-progress',
                                              willRenderArrivedButtons: appointmentStatus === 'arrived',
                                              willRenderMarkArrivedButton: appointmentStatus === 'scheduled'
                                            })
                                          }
                                          return null
                                        })()}
                                        {appointmentStatus === 'arrived' ? (
                                          <>
                                            <button
                                              className="pa-slot-start-consultation-btn"
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                handleStartConsultation(appointment, e)
                                              }}
                                              title="Start consultation"
                                            >
                                              🏥
                                            </button>
                                            <button
                                              className="pa-slot-revert-arrived-btn"
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                handleRevertArrived(appointment, e)
                                              }}
                                              title="Revert arrived status"
                                            >
                                              ↶
                                            </button>
                                          </>
                                        ) : appointmentStatus === 'in-progress' ? null : appointmentStatus === 'scheduled' ? (
                                          <>
                                            <button
                                              key={`arrived-btn-${appointment.id}-${appointmentStatus}`}
                                              className="pa-slot-arrived-btn"
                                              onClick={(e) => {
                                                console.log('[Debug] Mark as arrived button clicked for:', appointment.id, 'Status:', appointmentStatus, 'Raw status:', appointment.status)
                                                e.stopPropagation()
                                                e.preventDefault()
                                                handleMarkArrived(appointment, e)
                                              }}
                                              onMouseDown={(e) => {
                                                console.log('[Debug] Mark as arrived button mousedown for:', appointment.id)
                                                e.stopPropagation()
                                              }}
                                              title="Mark as arrived"
                                              style={{ display: appointmentStatus === 'scheduled' ? 'flex' : 'none' }}
                                            >
                                              ✓
                                            </button>
                                            <button
                                              key={`dna-btn-${appointment.id}-${appointmentStatus}`}
                                              className="pa-slot-dna-btn"
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                handleMarkDNA(appointment, e)
                                              }}
                                              title="Mark as DNA"
                                              style={{ display: appointmentStatus === 'scheduled' ? 'flex' : 'none' }}
                                            >
                                              ⚠️
                                            </button>
                                            <button
                                              key={`cancel-btn-${appointment.id}-${appointmentStatus}`}
                                              className="pa-slot-cancel-btn"
                                              onClick={(e) => {
                                                e.stopPropagation()
                                                handleCancel(appointment, e)
                                              }}
                                              title="Cancel appointment"
                                              style={{ display: appointmentStatus === 'scheduled' ? 'flex' : 'none' }}
                                            >
                                              ✕
                                            </button>
                                          </>
                                        ) : null}
                                      </div>
                                    )}
                                  </div>
                                </div>
                              )
                            })}
                          </div>
                        ) : (
                          <>
                            {isStart ? (
                              <div className={`pa-slot-booked-content ${primaryAppointment.status === 'arrived' ? 'pa-slot-arrived' : primaryAppointment.status === 'in-progress' ? 'pa-slot-in-progress' : primaryAppointment.status === 'completed' ? 'pa-slot-completed' : ''}`}>
                                {primaryAppointment.status === 'completed' && primaryAppointment.stcSuggested && (
                                  primaryAppointment.stcRaised ? (
                                    <span
                                      className="pa-slot-stc-badge pa-slot-stc-badge-disabled"
                                      title={primaryAppointment.stcRaisedAt ? `STC signed ${new Date(primaryAppointment.stcRaisedAt).toLocaleString()}` : 'STC signed'}
                                      aria-label="STC signed"
                                    >
                                      STC
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      className="pa-slot-stc-badge pa-slot-stc-badge-button"
                                      title={primaryAppointment.stcSuggestedReasons?.length ? `STC signature needed: ${primaryAppointment.stcSuggestedReasons.join(', ')}` : 'STC signature needed'}
                                      aria-label="STC signature required – open form"
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        setStcModalAppointment(primaryAppointment)
                                      }}
                                    >
                                      STC
                                    </button>
                                  )
                                )}
                                {primaryAppointment.status === 'arrived' && (
                                  <div className="pa-slot-arrived-badge">✓ Arrived</div>
                                )}
                                {primaryAppointment.status === 'in-progress' && (
                                  <div className="pa-slot-in-progress-badge">⚡ In Progress</div>
                                )}
                                {primaryAppointment.status === 'completed' && (
                                  <div className="pa-slot-completed-badge">✓ Completed</div>
                                )}
                                <div className="pa-slot-title">
                                  {formatPatientName(patients.find((p) => p.id === primaryAppointment.patientId) as any) || 'Unknown Patient'}
                                </div>
                                <div className="pa-slot-practitioner">
                                  {staff.find((s) => s.id === primaryAppointment.staffId) 
                                    ? formatUserName(staff.find((s) => s.id === primaryAppointment.staffId) as any)
                                    : 'Unknown Practitioner'}
                                </div>
                                <div className="pa-slot-time">
                                  {primaryAppointment.startTime} - {primaryAppointment.endTime}
                                </div>
                                {primaryAppointment.type && (
                                  <div className="pa-slot-type">{primaryAppointment.type}</div>
                                )}
                                {primaryAppointment.reason && (
                                  <div className="pa-slot-reason">{primaryAppointment.reason}</div>
                                )}
                                {primaryAppointment.status !== 'cancelled' && primaryAppointment.status !== 'completed' && primaryAppointment.status !== 'dna' && primaryAppointment.status !== 'in-progress' && (
                                  <div className="pa-slot-actions">
                                    {primaryAppointment.status === 'arrived' ? (
                                      <>
                                        <button
                                          className="pa-slot-start-consultation-btn"
                                          onClick={(e) => handleStartConsultation(primaryAppointment, e)}
                                          title="Start consultation"
                                        >
                                          🏥
                                        </button>
                                        <button
                                          className="pa-slot-revert-arrived-btn"
                                          onClick={(e) => handleRevertArrived(primaryAppointment, e)}
                                          title="Revert arrived status"
                                        >
                                          ↶
                                        </button>
                                      </>
                                    ) : primaryAppointment.status === 'scheduled' ? (
                                      <>
                                        <button
                                          className="pa-slot-arrived-btn"
                                          onClick={(e) => {
                                            console.log('[Debug] Single appointment - Mark as arrived clicked for:', primaryAppointment.id, 'Status:', primaryAppointment.status)
                                            handleMarkArrived(primaryAppointment, e)
                                          }}
                                          title="Mark as arrived"
                                        >
                                          ✓
                                        </button>
                                        <button
                                          className="pa-slot-dna-btn"
                                          onClick={(e) => handleMarkDNA(primaryAppointment, e)}
                                          title="Mark as DNA"
                                        >
                                          ⚠️
                                        </button>
                                        <button
                                          className="pa-slot-cancel-btn"
                                          onClick={(e) => handleCancel(primaryAppointment, e)}
                                          title="Cancel appointment"
                                        >
                                          ✕
                                        </button>
                                      </>
                                    ) : null}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div className="pa-slot-continuation-content"></div>
                            )}
                          </>
                        )
                      ) : slotStatus === 'available' ? (
                        <div className="pa-slot-available">Available</div>
                      ) : slotStatus === 'closed' ? (
                        <div className="pa-slot-closed">Closed</div>
                      ) : (
                        <div className="pa-slot-unavailable">—</div>
                      )}
                    </div>
                  )
                })
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      {showBookingModal && selectedSlot && (
        <Modal
          isOpen={showBookingModal}
          onClose={() => {
            setShowBookingModal(false)
            setSelectedSlot(null)
          }}
          title="Book Appointment"
          size="medium"
        >
          <AppointmentScheduler
            initialStaffId={selectedSlot.staffId}
            initialDate={selectedSlot.date}
            initialStartTime={selectedSlot.time}
            initialPatientId=""
            onSuccess={() => {
              setShowBookingModal(false)
              setSelectedSlot(null)
            }}
            onCancel={() => {
              setShowBookingModal(false)
              setSelectedSlot(null)
            }}
          />
        </Modal>
      )}

      {cancelModalAppointment && (
        <AppointmentCancelModal
          appointment={{
            id: cancelModalAppointment.id,
            patientName: formatPatientName(patients.find((p) => p.id === cancelModalAppointment.patientId) as any) || 'Unknown Patient',
            staffName: formatUserName(staff.find((s) => s.id === cancelModalAppointment.staffId) as any) || 'Unknown Staff',
            appointmentDate: cancelModalAppointment.appointmentDate,
            startTime: cancelModalAppointment.startTime,
            type: cancelModalAppointment.type,
          }}
          isOpen={!!cancelModalAppointment}
          onClose={() => setCancelModalAppointment(null)}
          onConfirm={handleCancelConfirm}
          isLoading={cancelMutation.isPending}
        />
      )}

      {dnaModalAppointment && (
        <AppointmentDNAModal
          appointment={{
            id: dnaModalAppointment.id,
            patientName: formatPatientName(patients.find((p) => p.id === dnaModalAppointment.patientId) as any) || 'Unknown Patient',
            staffName: formatUserName(staff.find((s) => s.id === dnaModalAppointment.staffId) as any) || 'Unknown Staff',
            appointmentDate: dnaModalAppointment.appointmentDate,
            startTime: dnaModalAppointment.startTime,
            type: dnaModalAppointment.type,
          }}
          isOpen={!!dnaModalAppointment}
          onClose={() => setDnaModalAppointment(null)}
          onConfirm={handleDNAConfirm}
          isLoading={dnaMutation.isPending}
        />
      )}

      {consultationModalAppointment && (
        <Modal
          isOpen={!!consultationModalAppointment}
          onClose={() => setConsultationModalAppointment(null)}
          title="Start Consultation"
          size="medium"
        >
          <QuickStartConsultationForm
            patientId={consultationModalAppointment.patientId}
            appointmentId={consultationModalAppointment.id}
            initialStaffId={consultationModalAppointment.staffId}
            initialReason={consultationModalAppointment.reason}
            onCancel={() => setConsultationModalAppointment(null)}
          />
        </Modal>
      )}

      {stcModalAppointment && stcModalAppointment.stcSuggestedConsultationId && (
        <Modal
          isOpen={!!stcModalAppointment}
          onClose={() => setStcModalAppointment(null)}
          title={`STC – ${formatPatientName(patients.find((p) => p.id === stcModalAppointment.patientId) as any) || 'Patient'}`}
          size="large"
        >
          <div className="stc-modal-body">
            <StcSigning
              embedded
              consultationIdProp={stcModalAppointment.stcSuggestedConsultationId}
              patientIdProp={stcModalAppointment.patientId}
              patientNameProp={formatPatientName(patients.find((p) => p.id === stcModalAppointment.patientId) as any) || undefined}
              initialLocationProp="S"
              initialSpecialServiceProp={mapStcReasonsToSpecialServiceCode(stcModalAppointment.stcSuggestedReasons)}
              appointmentIdProp={stcModalAppointment.id}
              onClose={() => setStcModalAppointment(null)}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ['stc-records'] })
                queryClient.invalidateQueries({ queryKey: ['appointments'] })
                setStcModalAppointment(null)
              }}
            />
            <p className="stc-modal-link">
              <Link to={`/patients/${stcModalAppointment.patientId}`} onClick={() => setStcModalAppointment(null)}>
                View patient record
              </Link>
            </p>
          </div>
        </Modal>
      )}
    </div>
  )
}



























